import { AlertCircle, ChevronRight, MessageSquareWarning, Package } from "lucide-react";
import { cn } from "@/lib/cn";
import type { ComplaintListItem } from "@/api/types";
import { ComplaintStageBadge, SeverityBadge, SlaBadge } from "./badges";
import { CHANNEL_LABEL, TYPE_LABEL, formatDate } from "./constants";

export function ComplaintRow({
  complaint: c,
  onOpen,
}: {
  complaint: ComplaintListItem;
  onOpen: () => void;
}) {
  const product =
    c.cCProductArticle || c.cCBatch || c.cCBatchUnknown || c.cCAffectedQuantity;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group flex min-h-[72px] items-center gap-3 rounded-lg border bg-background px-3.5 py-3 transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
    >
      <div
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
          c.severity === "critical"
            ? "bg-destructive/10 text-destructive"
            : "bg-muted text-muted-foreground",
        )}
      >
        <MessageSquareWarning className="h-[18px] w-[18px]" aria-hidden />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">{c.number || "Без номера"}</span>
          <ComplaintStageBadge stage={c.businessStage} state={c.currentState} />
          <SeverityBadge severity={c.severity} />
          {/* COMP-LIFECYCLE W6 — surface the control deadline on the row for the
              actionable states (breached + «Скоро дедлайн»); the card shows full SLA. */}
          {(c.sla.breached || c.sla.status === "due_soon") && <SlaBadge sla={c.sla} />}
          {c.needsMyAction && (
            <span className="inline-flex items-center gap-1 rounded-full bg-warning px-2 py-0.5 text-[12px] font-medium text-warning-foreground">
              <AlertCircle className="h-2.5 w-2.5" aria-hidden /> Потребує вашої дії
            </span>
          )}
        </div>
        <p className="mt-1 truncate text-sm text-foreground/90">{c.summary}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-muted-foreground">
          {c.account && <span className="truncate">{c.account.name}</span>}
          {c.complaintType && <span>· {TYPE_LABEL[c.complaintType]}</span>}
          {c.channel && <span>· {CHANNEL_LABEL[c.channel]}</span>}
          {c.receivedShort && <span>· {formatDate(c.receivedShort)}</span>}
          {c.assigneeName && <span>· {c.assigneeName}</span>}
          {c.assignmentStatus === "needs_manual_assignment" && (
            <span className="text-warning-foreground">· потребує призначення</span>
          )}
        </div>
        {product && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-muted-foreground">
            <Package className="h-3 w-3" aria-hidden />
            {c.cCProductArticle && <span>{c.cCProductArticle}</span>}
            {c.cCBatch && <span>· партія {c.cCBatch}</span>}
            {c.cCBatchUnknown && <span className="text-warning-foreground">· партія невідома</span>}
            {c.cCAffectedQuantity && <span>· {c.cCAffectedQuantity}</span>}
          </div>
        )}
      </div>

      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" aria-hidden />
    </div>
  );
}
